/**
 * Catalog API
 * Functions for fetching GPC hierarchy, categories, and product search
 */

import { coreAPI } from './client';
import {
  ICE_VALUE_SEPARATOR,
  type Facets,
  type NumberRange,
} from '@teletienda/shared-ui-lib';
import type {
  GpcSegment,
  GpcHierarchyNode,
  GpcHierarchyWithPath,
  GpcBrickAttribute,
  SearchResponse,
  SearchProduct,
  CatalogSearchParams,
  CategoryTreeNode,
  PaginatedResponse,
} from '@/types/catalog';

/**
 * Options for fetching platform-wide facets.
 * CF-SEARCH-4: Core Frontend is the marketplace → no `merchantGuid` (platform-wide).
 */
export interface GetFacetsOptions {
  /** Category GUID to scope facets (reveals dynamic attributes + Icecat features) */
  categoryId?: string;
  /** Return only leaf categories (parity with Storefront `/catalog/all`) */
  leafOnly?: boolean;
}

/**
 * API service for catalog and GPC operations
 */
export const catalogAPI = {
  // ============================================================================
  // GPC Hierarchy
  // ============================================================================

  /**
   * Get all GPC segments (level 1)
   */
  async getGpcSegments(): Promise<PaginatedResponse<GpcSegment>> {
    return coreAPI.get<PaginatedResponse<GpcSegment>>('/catalog/gpc/segments');
  },

  /**
   * Get GPC hierarchy for a specific code (path + children)
   */
  async getGpcHierarchy(gpcCode: string): Promise<GpcHierarchyWithPath> {
    return coreAPI.get<GpcHierarchyWithPath>(`/catalog/gpc/hierarchy/${gpcCode}`);
  },

  /**
   * Get direct children of a GPC code
   */
  async getGpcChildren(gpcCode: string): Promise<PaginatedResponse<GpcHierarchyNode>> {
    return coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(`/catalog/gpc/children/${gpcCode}`);
  },

  /**
   * Get brick attributes for a GPC brick (level 4)
   */
  async getGpcBrickAttributes(brickCode: string): Promise<PaginatedResponse<GpcBrickAttribute>> {
    return coreAPI.get<PaginatedResponse<GpcBrickAttribute>>(`/catalog/gpc/bricks/${brickCode}/attributes`);
  },

  /**
   * Search GPC nodes by name
   */
  async searchGpc(query: string, level?: number, limit = 20): Promise<PaginatedResponse<GpcHierarchyNode>> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (level) params.append('level', String(level));
    return coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(`/catalog/gpc/search?${params}`);
  },

  // ============================================================================
  // Product Search
  // ============================================================================

  /**
   * Get products by GUIDs (batch endpoint for favorites display)
   *
   * @param productGuids - Array of platform product GUIDs
   * @returns Products found and list of not found GUIDs
   */
  async getProductsByGuids(productGuids: string[]): Promise<{
    products: SearchProduct[];
    not_found: string[];
  }> {
    // Handle empty array efficiently (no API call)
    if (productGuids.length === 0) {
      return { products: [], not_found: [] };
    }

    // API returns camelCase response
    interface ApiProduct {
      productGuid: string;
      merchantGuid: string;
      name: string;
      description?: string;
      brand?: { guid: string; name: string; slug: string };
      category?: { guid: string; name: string; path?: string; level?: number };
      price: number;
      compareAtPrice?: number;
      currency: string;
      stockStatus?: string;
      primaryImageUrl?: string;
      sku?: string;
      gtin?: string;
      mpn?: string;
      attributes?: Record<string, unknown>;
      createdAt?: string;
      publishedAt?: string;
      averageRating?: number | null;
      totalReviews?: number;
    }

    interface ApiResponse {
      products: ApiProduct[];
      not_found: string[];
    }

    const raw = await coreAPI.post<ApiResponse>('/catalog/search/products/batch', {
      product_guids: productGuids,
    });

    // Transform camelCase to snake_case SearchProduct
    const products: SearchProduct[] = (raw.products || []).map((p): SearchProduct => ({
      product_guid: p.productGuid,
      merchant_guid: p.merchantGuid,
      name: p.name,
      description: p.description,
      brand: p.brand ? { guid: p.brand.guid, name: p.brand.name, slug: p.brand.slug } : undefined,
      category: p.category ? { guid: p.category.guid, name: p.category.name, path: p.category.path || '', level: p.category.level || 0 } : undefined,
      price: p.price,
      compare_at_price: p.compareAtPrice,
      currency: p.currency,
      stock_status: (p.stockStatus as SearchProduct['stock_status']) ?? 'in_stock',
      primary_image_url: p.primaryImageUrl,
      sku: p.sku || '',
      gtin: p.gtin,
      mpn: p.mpn,
      attributes: p.attributes || {},
      images: [],
      created_at: p.createdAt || new Date().toISOString(),
      updated_at: p.createdAt || new Date().toISOString(),
      average_rating: p.averageRating ?? null,
      total_reviews: p.totalReviews ?? 0,
    }));

    return {
      products,
      not_found: raw.not_found || [],
    };
  },

  /**
   * Get platform-wide facets for product filtering.
   *
   * CF-SEARCH-4: Core Frontend is the marketplace, so this calls
   * `GET catalog/search/facets` WITHOUT `merchantGuid` → platform-wide facets.
   * With no `categoryId`: categories + brands + price range. Once a category is
   * selected, the backend enriches the response with dynamic GPC attributes and
   * Icecat features — same UX as the Storefront `/catalog` index.
   *
   * The shared `Facets` shape (CF-SEARCH-2) is exactly what the endpoint returns
   * under `data.facets`; `coreAPI` unwraps the `{ success, data }` envelope.
   */
  async getFacets(options: GetFacetsOptions = {}): Promise<Facets> {
    const { categoryId, leafOnly } = options;
    const searchParams = new URLSearchParams();

    if (categoryId) searchParams.set('categoryGuid', categoryId);
    if (leafOnly) searchParams.set('leafOnly', 'true');
    // NB: no merchantGuid — Core is platform-wide.

    const query = searchParams.toString();
    const url = query
      ? `/catalog/search/facets?${query}`
      : '/catalog/search/facets';

    const response = await coreAPI.get<{ facets: Facets }>(url, false);
    return response.facets;
  },

  /**
   * Get the curated category tree (CF-SEARCH-6).
   *
   * Calls the PUBLIC `GET /catalog/categories/tree` (no auth) — the intentional
   * platform taxonomy used as the `/search` category source instead of
   * product-derived facets. Honours the user's language via `Accept-Language`
   * (set by `coreAPI`). The endpoint returns the same `{ items, total }` tree DTO
   * as `categories?mode=tree`; we keep only the nav-relevant fields.
   */
  async getCategoryTree(): Promise<CategoryTreeNode[]> {
    interface ApiTreeNode {
      category_guid: string;
      parent_guid: string | null;
      level: number;
      name: string;
      slug: string;
      path: string;
      children?: ApiTreeNode[];
    }

    const mapNode = (node: ApiTreeNode): CategoryTreeNode => ({
      category_guid: node.category_guid,
      parent_guid: node.parent_guid,
      level: node.level,
      name: node.name,
      slug: node.slug,
      path: node.path,
      children: (node.children ?? []).map(mapNode),
    });

    const response = await coreAPI.get<{ items: ApiTreeNode[]; total: number }>(
      '/catalog/categories/tree',
      false
    );
    return (response.items ?? []).map(mapNode);
  },

  /**
   * Serialize search params to the catalog `SearchController` query contract.
   *
   * Extracted so the input→query-string mapping is unit-testable (CF-SEARCH-4,
   * per repo "assert invariants, not shapes"). Mirrors the working Storefront
   * client: `price_min`/`price_max`, repeated `brand=`, `attributes[slug]=…`,
   * `ice_*`, and `stock_status` for in-stock.
   */
  buildSearchQuery(params: CatalogSearchParams): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (params.q) searchParams.set('q', params.q);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.sort) searchParams.set('sort', params.sort);

    // Brands: prefer multi (repeated `brand=`), fall back to single.
    if (params.brands?.length) {
      params.brands.forEach((b) => searchParams.append('brand', b));
    } else if (params.brand) {
      searchParams.set('brand', params.brand);
    }

    if (params.category) searchParams.set('category', params.category);
    if (params.gpc_code) searchParams.set('gpc_code', params.gpc_code);

    // Price: backend expects price_min / price_max (NOT min_price / max_price).
    if (params.price_min !== undefined) searchParams.set('price_min', String(params.price_min));
    if (params.price_max !== undefined) searchParams.set('price_max', String(params.price_max));

    // In-stock: backend filters on stock_status, not a boolean `in_stock`.
    if (params.in_stock) searchParams.set('stock_status', 'in_stock');

    // Dynamic attribute filters → attributes[slug]=v1,v2 (range → _min/_max).
    if (params.attrs) {
      for (const [slug, value] of Object.entries(params.attrs)) {
        if (value === null || value === undefined) continue;

        if (Array.isArray(value)) {
          if (value.length > 0) searchParams.set(`attributes[${slug}]`, value.join(','));
        } else if (typeof value === 'boolean') {
          searchParams.set(`attributes[${slug}]`, String(value));
        } else if (typeof value === 'object' && ('min' in value || 'max' in value)) {
          const range = value as NumberRange;
          if (range.min !== null && range.min !== undefined) {
            searchParams.set(`attributes[${slug}_min]`, String(range.min));
          }
          if (range.max !== null && range.max !== undefined) {
            searchParams.set(`attributes[${slug}_max]`, String(range.max));
          }
        }
      }
    }

    // ICE-14 Icecat feature filters → ice_code=v1|v2 or ice_code_min/_max.
    if (params.icecatFeatures) {
      for (const [code, filterValue] of Object.entries(params.icecatFeatures)) {
        if (!filterValue) continue;

        if (filterValue.values && filterValue.values.length > 0) {
          searchParams.set(`ice_${code}`, filterValue.values.join(ICE_VALUE_SEPARATOR));
        } else if (filterValue.range) {
          if (filterValue.range.min !== undefined) {
            searchParams.set(`ice_${code}_min`, String(filterValue.range.min));
          }
          if (filterValue.range.max !== undefined) {
            searchParams.set(`ice_${code}_max`, String(filterValue.range.max));
          }
        }
      }
    }

    return searchParams;
  },

  /**
   * Search products with GPC filtering
   */
  async searchProducts(params: CatalogSearchParams): Promise<SearchResponse> {
    const searchParams = catalogAPI.buildSearchQuery(params);

    // API returns camelCase response, transform to snake_case SearchResponse
    interface ApiProduct {
      productGuid: string;
      merchantGuid: string;
      modelGuid?: string;
      name: string;
      description?: string;
      brand?: { guid: string; name: string; slug: string };
      category?: { guid: string; name: string; path: string; level?: number };
      gpcBrickCode?: string;
      gpcSegmentCode?: string;
      gpcFamilyCode?: string;
      gpcClassCode?: string;
      gpcHierarchyPath?: string[];
      gpcBrickName?: string;
      brickAttributes?: Array<{ code: string; value_code: string; value_text: string }>;
      gtin?: string;
      mpn?: string;
      sku: string;
      attributes: Record<string, unknown>;
      price: number;
      compareAtPrice?: number;
      currency: string;
      stockStatus?: string;
      images?: Array<{ url: string; position: number; is_primary: boolean }>;
      primaryImageUrl?: string;
      createdAt: string;
      updatedAt?: string;
      averageRating?: number | null;
      totalReviews?: number;
    }

    interface ApiResponse {
      products: ApiProduct[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }

    const raw = await coreAPI.get<ApiResponse>(`/catalog/search/products?${searchParams}`, false);

    // Transform to SearchResponse with snake_case fields
    return {
      items: (raw.products || []).map((p): SearchProduct => ({
        product_guid: p.productGuid,
        merchant_guid: p.merchantGuid,
        model_guid: p.modelGuid,
        name: p.name,
        description: p.description,
        brand: p.brand ? { guid: p.brand.guid, name: p.brand.name, slug: p.brand.slug } : undefined,
        category: p.category ? { guid: p.category.guid, name: p.category.name, path: p.category.path, level: p.category.level || 0 } : undefined,
        gpc_brick_code: p.gpcBrickCode,
        gpc_segment_code: p.gpcSegmentCode,
        gpc_family_code: p.gpcFamilyCode,
        gpc_class_code: p.gpcClassCode,
        gpc_hierarchy_path: p.gpcHierarchyPath,
        gpc_brick_name: p.gpcBrickName,
        brick_attributes: p.brickAttributes,
        gtin: p.gtin,
        mpn: p.mpn,
        sku: p.sku || '',
        attributes: p.attributes || {},
        price: p.price,
        compare_at_price: p.compareAtPrice,
        currency: p.currency,
        stock_status: (p.stockStatus as SearchProduct['stock_status']) ?? 'in_stock',
        images: p.images || [],
        primary_image_url: p.primaryImageUrl,
        created_at: p.createdAt,
        updated_at: p.updatedAt || p.createdAt,
        average_rating: p.averageRating ?? null,
        total_reviews: p.totalReviews ?? 0,
      })),
      total: raw.pagination?.total || 0,
      page: raw.pagination?.page || 1,
      limit: raw.pagination?.limit || 20,
    };
  },
};

export default catalogAPI;
